/**
 * RealCampaignRecallsPanel
 * ──────────────────────────────────────────────────────────────────────
 * Real-mode (API-backed) twin of `CampaignRecallsPanel`. Same visual
 * structure as the demo component — campaign picker → campaign detail
 * with CSV/outreach CTAs → rule builder → group chips → demographics +
 * roster table — but with ZERO patient fixtures in the bundle.
 *
 * Data sources:
 *   • Campaign / rule / group catalog: CLINICAL APP METADATA (the same
 *     5 campaigns / 6 rules / 6 groups the platform supports regardless
 *     of mode — same status as visit-type catalog or RBAC role enum).
 *     This is NOT patient data, NOT scraped from demo fixtures.
 *   • Patient roster: `GET /api/scheduler/recall/roster` with
 *     `campaign_id` + `rules[]` + `group` params. Renders an empty
 *     state w/ structured CTA when the endpoint hasn't been
 *     provisioned yet (404 / 501 / missing).
 *   • Demographics: derived client-side from the returned roster — no
 *     second endpoint required.
 *   • CSV export: client-side from the same roster.
 *   • Outreach: `POST /api/scheduler/recall/outreach` — toast on
 *     success, toast w/ instructions when the endpoint is missing.
 *
 * ACL: any authenticated role with `scheduler:read` may VIEW. Send
 * outreach gated on `scheduler:write` (front_desk / admin / manager).
 *
 * Empty / error / loading states are first-class and always render
 * the layout chrome so the page never collapses to a blank panel.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import {
  Users, Download, Plus, Activity, Heart, Syringe, Shield, Calendar,
  Sparkles, Filter, TrendingUp, RefreshCw, Loader2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { get as httpGet, post as httpPost } from '../../../lib/api/httpClient';
import { useReportSchedulerSource } from '../../../lib/scheduler/SchedulerTraceContext';

/* ── CLINICAL APP METADATA (not a patient fixture) ────────────────── */
const CAMPAIGNS = [
  { id: 'apcm',     name: 'APCM',            full: 'APCM · Advanced Primary Care Management', icon: Heart,    accent: '#ef4444', desc: 'Chronic-care eligible, ≥ 2 conditions, CMS-reimbursable monthly touch-point.' },
  { id: 'ccm',      name: 'CCM',             full: 'CCM · Chronic Care Management',           icon: Activity, accent: '#6b5ce7', desc: 'Enrolled CCM patients — 20-min / month care plan outreach.' },
  { id: 'wellness', name: 'Annual Wellness', full: 'Annual Wellness',                          icon: Calendar, accent: '#22c55e', desc: 'Medicare AWV overdue patients, prep + book.' },
  { id: 'allergy',  name: 'Allergy Shots',   full: 'Allergy Shots',                            icon: Syringe,  accent: '#f59e0b', desc: 'Immunotherapy patients on weekly/biweekly schedule.' },
  { id: 'flu',      name: 'Flu Vaccine',     full: 'Flu Vaccine',                              icon: Shield,   accent: '#0ea5e9', desc: 'Seasonal flu — eligible panel with no dose logged this season.' },
];

const RULES = [
  { id: 'not-seen-9mo',  label: 'Not seen in last 9 months',  kind: 'inactivity' },
  { id: 'not-seen-12mo', label: 'Not seen in last 12 months', kind: 'inactivity' },
  { id: 'dx-htn',        label: 'Dx: Hypertension (I10)',     kind: 'dx' },
  { id: 'dx-dm2',        label: 'Dx: Type-2 Diabetes (E11.9)', kind: 'dx' },
  { id: 'age-65plus',    label: 'Age ≥ 65',                   kind: 'demo' },
  { id: 'medicare',      label: 'Insurance: Medicare',         kind: 'payer' },
];

const GROUPS = [
  { id: 'annual',     label: 'Annual wellness' },
  { id: 'allergy',    label: 'Allergy' },
  { id: 'flu',        label: 'Flu' },
  { id: 'copd',       label: 'COPD panel' },
  { id: 'diabetes',   label: 'Diabetes panel' },
  { id: 'postpartum', label: 'Postpartum' },
];

const AGE_BANDS = [
  { label: '18-34', min: 18, max: 34 },
  { label: '35-49', min: 35, max: 49 },
  { label: '50-64', min: 50, max: 64 },
  { label: '65+',   min: 65, max: 200 },
];

const monthsSince = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const then = new Date(y, m - 1, d);
  const now = new Date();
  return (now.getFullYear() - then.getFullYear()) * 12 + (now.getMonth() - then.getMonth());
};

const RealCampaignRecallsPanel = ({ allowOutreach = true }) => {
  const [campaignId, setCampaignId] = useState('apcm');
  const [selectedRules, setSelectedRules] = useState(['not-seen-9mo']);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiMissing, setApiMissing] = useState(false);

  const campaign = CAMPAIGNS.find((c) => c.id === campaignId);

  // Real-mode roster fetch — endpoint is `/api/scheduler/recall/roster`.
  // The recall API isn't provisioned in every vendor build yet; on
  // 404/501 we flip into `apiMissing` and render a structured empty
  // state with the endpoint contract for backend follow-up.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const res = await httpGet('/api/scheduler/recall/roster', {
        campaign_id: campaignId,
        rules: selectedRules.join(',') || undefined,
        group: selectedGroup || undefined,
      });
      if (cancelled) return;
      if (res?.ok) {
        const list = Array.isArray(res.data)
          ? res.data
          : (res.data?.roster || res.data?.patients || res.data?.results || []);
        setRoster(list);
        setApiMissing(false);
      } else {
        const status = res?.error?.status ?? null;
        setRoster([]);
        if (status === 404 || status === 501) {
          setApiMissing(true);
          setError(null);
        } else {
          setApiMissing(false);
          setError(res?.error || { message: 'Failed to load roster' });
        }
      }
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [campaignId, selectedRules, selectedGroup]);

  const toggleRule = (id) =>
    setSelectedRules((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  /* Demographics derived client-side from whatever the roster API
     returned. With zero rows it shows 0% bars — matches Legacy chrome
     without ever displaying made-up numbers. */
  const demo = useMemo(() => {
    const total = roster.length || 0;
    const byAge = AGE_BANDS.map((b) => ({
      ...b,
      count: roster.filter((p) => Number(p.age) >= b.min && Number(p.age) <= b.max).length,
    }));
    const female = roster.filter((p) => String(p.sex || '').toUpperCase() === 'F').length;
    const male   = roster.filter((p) => String(p.sex || '').toUpperCase() === 'M').length;
    const safeTotal = total || 1;
    return {
      byAge, total,
      female, male,
      femalePct: Math.round((female / safeTotal) * 100),
      malePct:   Math.round((male   / safeTotal) * 100),
    };
  }, [roster]);

  const exportCsv = () => {
    if (roster.length === 0) { toast.info('Roster is empty — nothing to export.'); return; }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = 'Patient,Age,Sex,Last seen,Months since,Insurance';
    const body = roster.map((p) => {
      const last = p.last_seen || p.lastSeen || '';
      return [esc(p.name || p.patient_name), p.age ?? '', p.sex ?? '', last, monthsSince(last) ?? '', esc(p.insurance)].join(',');
    }).join('\n');
    const blob = new Blob([`${header}\n${body}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `recall-${campaignId}-roster.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${roster.length} patient(s) · ${campaign.name}`);
  };

  const sendOutreach = async () => {
    if (!allowOutreach) { toast.info('Your role can view recall campaigns but not send outreach.'); return; }
    if (roster.length === 0) { toast.info('No patients in roster — nothing to send.'); return; }
    const res = await httpPost('/api/scheduler/recall/outreach', {
      campaign_id: campaignId,
      rules: selectedRules,
      group: selectedGroup,
      patient_ids: roster.map((p) => p.id || p.patient_id).filter(Boolean),
    });
    if (res?.ok) {
      toast.success(`Queued outreach for ${roster.length} patient(s)`, {
        description: `Campaign: ${campaign.name} · Rules: ${selectedRules.length}`,
      });
    } else if (res?.error?.status === 404 || res?.error?.status === 501) {
      toast.info('Outreach endpoint not yet provisioned', {
        description: 'POST /api/scheduler/recall/outreach is missing. Backend needs to ship the endpoint to enable queue-and-send.',
      });
    } else {
      toast.error('Outreach failed', { description: res?.error?.message || 'Try again' });
    }
  };

  /* Single source-of-truth for the data state — surfaced on the root
     as `data-recall-source` so QA / ops can grep one attribute to know
     why the panel is empty or loud. Values:
       loading      → request in flight
       live         → roster rows came back
       empty        → API ok, 0 rows match the current rule set
       api_missing  → enriched 404/501 / endpoint not provisioned
       error        → any other non-2xx (auth, 5xx, network)             */
  const recallSource =
    loading      ? 'loading'
    : apiMissing ? 'api_missing'
    : error      ? 'error'
    : roster.length > 0 ? 'live'
    : 'empty';
  useReportSchedulerSource('recall', recallSource);

  return (
    <div className="space-y-4" data-testid="real-campaign-recalls-panel" data-recall-source={recallSource}>
      {/* ─── Campaign picker ───────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-slate-800">Recall campaigns</p>
          <Button
            size="sm" variant="outline"
            className="h-7 text-[10px] gap-1"
            onClick={() => toast.info('Custom campaign builder — POST /api/scheduler/recall/campaigns not yet provisioned')}
            data-testid="real-campaign-new-btn"
          >
            <Plus className="h-3 w-3" /> New campaign
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="real-campaign-grid">
          {CAMPAIGNS.map((c) => {
            const on = c.id === campaignId;
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => setCampaignId(c.id)}
                className={cn(
                  'text-left p-3 rounded-xl border transition-all',
                  on ? 'border-slate-900 bg-slate-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300',
                )}
                style={{ cursor: 'pointer' }}
                data-testid={`real-campaign-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-7 w-7 rounded-md inline-flex items-center justify-center" style={{ background: `${c.accent}1a` }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: c.accent }} />
                  </div>
                  <Badge className="text-[9px] border border-slate-200 bg-slate-100 text-slate-700">
                    {on && roster.length > 0 ? roster.length : '—'}
                  </Badge>
                </div>
                <p className="text-[11px] font-semibold text-slate-900 mt-2">{c.name}</p>
                <p className="text-[9px] text-slate-500 mt-0.5 line-clamp-2">{c.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Campaign detail + rule + groups ──────────────────── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="real-campaign-detail">
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <campaign.icon className="h-4 w-4 shrink-0" style={{ color: campaign.accent }} />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-slate-900 truncate" data-testid="real-campaign-detail-title">{campaign.full}</p>
              <p className="text-[10px] text-slate-500 truncate" data-testid="real-campaign-detail-desc">{campaign.desc}</p>
            </div>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={exportCsv} data-testid="real-campaign-export-csv" disabled={roster.length === 0}>
              <Download className="h-3 w-3" /> CSV
            </Button>
            <Button size="sm" className="h-7 text-[10px] bg-[#111] hover:bg-[#222] gap-1" onClick={sendOutreach} data-testid="real-campaign-send-outreach" disabled={!allowOutreach || roster.length === 0}>
              <Sparkles className="h-3 w-3" /> Send outreach ({roster.length})
            </Button>
          </div>
        </div>

        {/* Rule builder */}
        <div className="space-y-1.5">
          <p className="text-[9px] uppercase font-semibold text-slate-400 inline-flex items-center gap-1">
            <Filter className="h-2.5 w-2.5" /> Recall triggers
          </p>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="real-rule-row">
            {RULES.map((r) => {
              const on = selectedRules.includes(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => toggleRule(r.id)}
                  className={cn('text-[10px] px-2.5 py-1 rounded-full border transition-colors',
                    on ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100')}
                  style={{ cursor: 'pointer' }}
                  data-testid={`real-rule-${r.id}`}
                >{r.label}</button>
              );
            })}
          </div>
        </div>

        {/* Group chips */}
        <div className="space-y-1.5 mt-3">
          <p className="text-[9px] uppercase font-semibold text-slate-400 inline-flex items-center gap-1">
            <Users className="h-2.5 w-2.5" /> Patient groups
          </p>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="real-group-row">
            <button
              onClick={() => setSelectedGroup(null)}
              className={cn('text-[10px] px-2.5 py-1 rounded-full border transition-colors',
                !selectedGroup ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100')}
              style={{ cursor: 'pointer' }}
              data-testid="real-group-all"
            >All patients</button>
            {GROUPS.map((g) => (
              <button
                key={g.id}
                onClick={() => setSelectedGroup(g.id === selectedGroup ? null : g.id)}
                className={cn('text-[10px] px-2.5 py-1 rounded-full border transition-colors',
                  selectedGroup === g.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100')}
                style={{ cursor: 'pointer' }}
                data-testid={`real-group-${g.id}`}
              >{g.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── API-missing structured empty state ───────────────── */}
      {apiMissing && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3" data-testid="real-recall-api-missing">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[12px] font-semibold text-amber-900">Recall API not yet provisioned</p>
            <p className="text-[10px] text-amber-700 mt-0.5">
              The Recall UI is wired and ready to consume the following endpoints — backend can ship them with zero front-end change:
            </p>
            <ul className="text-[10px] text-amber-700 mt-1.5 space-y-0.5 list-disc list-inside">
              <li><code>GET /api/scheduler/recall/roster?campaign_id=…&amp;rules=…&amp;group=…</code></li>
              <li><code>POST /api/scheduler/recall/outreach</code> (body: <code>{`{ campaign_id, rules[], group, patient_ids[] }`}</code>)</li>
              <li><code>POST /api/scheduler/recall/campaigns</code> (custom campaign builder)</li>
            </ul>
          </div>
        </div>
      )}

      {/* ─── Demographics + roster ─────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="real-campaign-demographics">
          <p className="text-[11px] font-semibold text-slate-800 inline-flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3 text-slate-500" /> Demographics
          </p>
          <p className="text-[9px] text-slate-400 mt-0.5">{demo.total} eligible patient{demo.total === 1 ? '' : 's'}</p>
          <div className="space-y-2 mt-3">
            {demo.byAge.map((b) => {
              const pct = demo.total ? Math.round((b.count / demo.total) * 100) : 0;
              return (
                <div key={b.label} data-testid={`real-demo-age-${b.label}`}>
                  <div className="flex items-center justify-between text-[10px] text-slate-600 mb-0.5">
                    <span>{b.label}</span>
                    <span className="tabular-nums">{b.count} · {pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: campaign.accent }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-center">
            <div className="p-2 rounded-lg bg-rose-50 border border-rose-100">
              <p className="text-[9px] text-rose-600 uppercase font-semibold">Female</p>
              <p className="text-[14px] font-bold text-rose-800">{demo.femalePct}%</p>
            </div>
            <div className="p-2 rounded-lg bg-sky-50 border border-sky-100">
              <p className="text-[9px] text-sky-600 uppercase font-semibold">Male</p>
              <p className="text-[14px] font-bold text-sky-800">{demo.malePct}%</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden lg:col-span-2" data-testid="real-campaign-roster">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <p className="text-[12px] font-semibold text-slate-900 inline-flex items-center gap-1.5">
              Eligible roster
              {loading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-testid="real-roster-loading" />}
            </p>
            <div className="flex items-center gap-1.5">
              <Badge className="text-[9px] border border-slate-200 bg-slate-100 text-slate-700">{roster.length}</Badge>
              <button
                type="button"
                onClick={() => setSelectedRules((r) => [...r])}  /* triggers re-fetch via useEffect deps */
                className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-0.5"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid="real-roster-refresh"
                title="Refresh roster"
              >
                <RefreshCw className="h-3 w-3" />
              </button>
            </div>
          </div>
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-[9px] text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-1.5">Patient</th>
                <th className="text-left px-3 py-1.5">Age</th>
                <th className="text-left px-3 py-1.5">Sex</th>
                <th className="text-left px-3 py-1.5">Last seen</th>
                <th className="text-left px-3 py-1.5">Gap</th>
                <th className="text-left px-3 py-1.5">Insurance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="text-center px-3 py-8 text-[11px] text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin inline-block mr-1.5" /> Loading roster…
                </td></tr>
              ) : error ? (
                <tr><td colSpan={6} className="text-center px-3 py-8 text-[11px] text-rose-600" data-testid="real-roster-error">
                  Failed to load roster: {error.message || 'unknown error'}
                </td></tr>
              ) : roster.length === 0 ? (
                <tr><td colSpan={6} className="text-center px-3 py-8 text-[11px] text-slate-400" data-testid="real-roster-empty">
                  {apiMissing ? 'Recall API not yet provisioned — roster will populate once it ships.' : 'No patients match this rule set. Relax a trigger above.'}
                </td></tr>
              ) : roster.map((p) => {
                const lastSeen = p.last_seen || p.lastSeen || '';
                const gap = monthsSince(lastSeen);
                return (
                  <tr key={p.id || p.patient_id} className="hover:bg-slate-50/60" data-testid={`real-roster-row-${p.id || p.patient_id}`}>
                    <td className="px-3 py-1.5 font-medium text-slate-800">{p.name || p.patient_name}</td>
                    <td className="px-3 py-1.5 text-slate-600">{p.age ?? '—'}</td>
                    <td className="px-3 py-1.5 text-slate-600">{p.sex ?? '—'}</td>
                    <td className="px-3 py-1.5 text-slate-600">{lastSeen || '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={cn('text-[10px] font-semibold',
                        gap == null ? 'text-slate-400'
                        : gap >= 12 ? 'text-red-600'
                        : gap >= 9  ? 'text-amber-700'
                        : 'text-slate-500')}>{gap == null ? '—' : `${gap} mo`}</span>
                    </td>
                    <td className="px-3 py-1.5 text-slate-500">{p.insurance ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RealCampaignRecallsPanel;
